import { useEffect, useMemo, useState } from 'react'
import Card from '../../components/Card'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import TabBar from '../../components/TabBar'
import { listReportRows, REPORT_ROW_KINDS } from './reportsService'
import { reportsStrings as S } from './strings'
import './ProgramReportPeople.css'

const PAGE_SIZE = 10

/**
 * Who is behind the four shares — the legacy report's drill-down tabs: the trainees who
 * passed, failed, did not finish and did not attend, and the same four per department.
 *
 * Server-paged, like the assessment report's people tabs: a programme can hold thousands of
 * trainees. The programme and the dates are the page's filter; any change to them starts
 * the list over.
 */
export default function ProgramReportPeople({ programId, fromDate, toDate }) {
  const [kind, setKind] = useState('success')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setPage(1)
  }, [programId, fromDate, toDate, kind, query])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    listReportRows({ kind, programId, fromDate, toDate, page, pageSize, search: query, signal: controller.signal })
      .then((result) => {
        setRows(result.rows)
        setTotal(result.total)
        setLoading(false)
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return
        setRows([])
        setTotal(0)
        setLoading(false)
      })
    return () => controller.abort()
  }, [kind, programId, fromDate, toDate, page, pageSize, query])

  const userColumns = useMemo(
    () => [
      { key: 'RowNumber', header: S.people.index, width: 56, sortable: false, align: 'center' },
      {
        key: 'FullName',
        header: S.people.user,
        sortable: false,
        render: (row) => (
          <span className="rpeople__user">
            <bdi>{row.FullName || row.UserName}</bdi>
            {row.FullName && (
              <span className="rpeople__username" dir="ltr">
                {row.UserName}
              </span>
            )}
          </span>
        ),
      },
      { key: 'Department', header: S.people.department, sortable: false, render: (row) => <bdi>{row.Department}</bdi> },
      {
        key: 'Email',
        header: S.people.email,
        sortable: false,
        render: (row) => <span dir="ltr">{row.Email}</span>,
      },
      ...(kind === 'success' || kind === 'failure'
        ? [
            {
              key: 'ExamDate',
              header: S.people.examDate,
              sortable: false,
              render: (row) => <span dir="ltr">{row.ExamDate ?? '—'}</span>,
            },
            { key: 'Degree', header: S.people.degree, sortable: false, align: 'center', render: (row) => row.Degree ?? '—' },
          ]
        : []),
      ...(kind === 'incomplete'
        ? [{ key: 'WatchPercentage', header: S.people.watched, sortable: false, align: 'center', render: (row) => <bdi>{row.WatchPercentage ?? '—'}</bdi> }]
        : []),
    ],
    [kind],
  )

  const groupColumns = useMemo(() => {
    const share = (count, percentage) => (row) => (
      <span className="rpeople__share">
        <span className="tabular">{row[count]}</span>
        <bdi className="rpeople__percentage">{row[percentage]}</bdi>
      </span>
    )
    return [
      { key: 'Name', header: S.people.department, sortable: false, render: (row) => <bdi>{row.Name}</bdi> },
      { key: 'UsersCount', header: S.people.users, sortable: false, align: 'center' },
      { key: 'SuccessCount', header: S.shares.success, sortable: false, align: 'center', render: share('SuccessCount', 'SuccessPercentage') },
      { key: 'FailureCount', header: S.shares.failure, sortable: false, align: 'center', render: share('FailureCount', 'FailurePercentage') },
      { key: 'UncompletedCount', header: S.shares.incomplete, sortable: false, align: 'center', render: share('UncompletedCount', 'UncompletedPercentage') },
      { key: 'AbsenceCount', header: S.shares.absent, sortable: false, align: 'center', render: share('AbsenceCount', 'AbsencePercentage') },
    ]
  }, [])

  return (
    <Card title={S.people.title}>
      <TabBar
        items={REPORT_ROW_KINDS.map((id) => ({ id, label: S.people.tabs[id] }))}
        value={kind}
        onChange={setKind}
        ariaLabel={S.people.title}
      />
      <FilterBar search={{ value: query, onChange: setQuery, placeholder: kind === 'groups' ? S.people.searchGroups : S.people.search }} />
      <DataTable
        /* The page's `?page` belongs to the courses table. */
        pageParam={null}
        stateKey={false}
        columns={kind === 'groups' ? groupColumns : userColumns}
        rows={rows}
        loading={loading}
        query={query}
        getRowId={(row) => `${kind}|${row.RowNumber}|${row.UserName ?? row.Name}`}
        emptyMessage={S.people.empty}
        server={{
          total,
          page,
          pageSize,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
          },
        }}
      />
    </Card>
  )
}
